/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  PropsWithChildren
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {toggleThemeStyle} from '../../src/theme/helpers';
import {dlsColor, dlsZIndex} from '../../src/theme/theme';

export interface BulkSelectionHeaderProps {
  show?: boolean;
  overrideClassName?: string;
}

export const useStyles = makeStyles(theme => ({
  selectionsContainer : {
    color           : dlsColor('white'),
    backgroundColor : toggleThemeStyle(dlsColor('grey1'), dlsColor('darkTeal')),
    height          : '40px',
    minHeight       : '40px',
    padding         : '5px 10px',
    display         : 'flex',
    alignItems      : 'center' as any,
    justifyContent  : 'space-between' as any
  },
  selectionContainerVisible : {
    visibility : 'visible' as any,
    opacity    : 1,
    transition : 'height 300ms ease-in-out',
    position   : '-webkit-sticky' as any,
    /* @ts-ignore: allow duplicate property for safari webkit */
    position   : 'sticky',
    top        : 0,
    zIndex     : dlsZIndex('selectionsHeaderSelectionContainerVisible') as any
  },
  selectionsContainerHidden : {
    opacity    : 0,
    height     : 0,
    padding    : 0,
    transition : 'height 300ms ease-in-out',
    visibility : 'hidden' as any
  }
}));

/**
 * Container responsible for displaying, hiding, showing, and styling a grid selection header.
 *
 * ```typescript
 *
 *   import {SelectionHeaderContainer} from '@ion/dls/src/grid/';
 *
 *   <SelectionHeaderContainer show={showHeader}>
 *    <SelectionHeaderButtons>
 *      <HeaderButton/>
 *      <HeaderButton/>
 *    </SelectionHeaderButtons>
 *  </SelectionHeaderContainer>
 *
 * ```
 */
export const SelectionHeaderContainer = ({
  children,
  overrideClassName,
  show = false
}: PropsWithChildren<BulkSelectionHeaderProps>) => {
  const classes = useStyles();

  return (
    <div className={classnames(
      classes.selectionsContainer,
      show ? classes.selectionContainerVisible : classes.selectionsContainerHidden,
      overrideClassName
    )}>
      {children}
    </div>
  );
};

export default memo(SelectionHeaderContainer);
